import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Car, Users, Receipt, UserCog, LogOut, Search, Bell } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/vehicles", label: "Inventory", icon: Car, testId: "nav-vehicles" },
  { to: "/customers", label: "Customers", icon: Users, testId: "nav-customers" },
  { to: "/sales", label: "Sales", icon: Receipt, testId: "nav-sales" },
  { to: "/staff", label: "Staff", icon: UserCog, testId: "nav-staff" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50">
      <Toaster position="top-right" richColors />

      {/* Sidebar */}
      <aside className="sidebar-dark w-[260px] fixed inset-y-0 left-0 flex flex-col z-30">
        <div className="px-6 pt-6 pb-6">
          <div className="flex items-center gap-3">
            <img src="/milex-logo.png" alt="Milex" className="w-11 h-11 object-contain" />
            <div>
              <div className="text-white font-bold text-lg tracking-widest" style={{ fontFamily: "Outfit", color: "#E5B84B" }}>MILEX</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">NZ Dealership DMS</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `sidebar-item flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium ${isActive ? "active" : ""}`
              }
            >
              <item.icon className="w-[18px] h-[18px]" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9 bg-blue-600">
              <AvatarFallback className="bg-blue-600 text-white text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wide text-blue-400 font-mono">{user?.role}</div>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-button"
              className="text-slate-400 hover:text-white p-2 rounded-md hover:bg-slate-800"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 ml-[260px]">
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center px-8 gap-4">
          <div className="flex-1 max-w-xl relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="global-search-input"
              placeholder="Search VIN, Rego, Stock # or vehicle…"
              className="w-full pl-9 pr-4 h-10 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) navigate(`/vehicles?q=${encodeURIComponent(e.target.value)}`); }}
            />
          </div>
          <button data-testid="notification-bell" className="p-2 rounded-md hover:bg-slate-100 text-slate-600 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
          </button>
        </header>
        <div className="p-8 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
